import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useMatch, useLocation } from 'react-router-dom';
import { Grid } from '@mui/material';
import { List as MenuIcon } from 'react-bootstrap-icons';
import classnames from 'classnames';
import { Helmet } from 'react-helmet';

import { useGetTraderClient } from 'api';
import { useRWD } from 'hooks';
import { reduxActions, useDispatch, useSelector } from 'store';
import { HeaderTopBar, BottomMenu } from 'components/layouts/MainLayout/components';
import { Loader } from 'components/controls';
import WiseTraderDashboardMenu from 'components/wise/WiseTraderDashboardMenu';
import TraderClientDashboardMenu from 'components/containers/TraderClientDashboardMenu';
import ChatLauncher from 'components/containers/ChatLauncher';
import DockedChatContainer from 'components/containers/DockedChatContainer';
import { isWiseAiEnabled } from 'config/wiseAi';

import styles from 'theme/components/layouts/TraderPanelLayout/TraderPanelLayout.module.scss';

const TraderPanelLayout = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isCondensed, setIsCondensed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isMobile } = useRWD();
  const clientRouteMatch = useMatch({ path: '/:locale/trader/clients/:id', end: false });
  const insightsClientRouteMatch = useMatch({ path: '/:locale/trader/insights/:clientId', end: false });
  const clientIdFromPath = clientRouteMatch?.params?.id ?? insightsClientRouteMatch?.params?.clientId;
  const isClientContext = Boolean(
    (clientRouteMatch || insightsClientRouteMatch) && clientIdFromPath && clientIdFromPath !== 'new'
  );
  const pathClientId =
    clientIdFromPath && clientIdFromPath !== 'new' ? Number(clientIdFromPath) : 0;
  const isValidPathClientId = Number.isFinite(pathClientId) && pathClientId > 0;

  const { currentClientId, currentClient } = useSelector((state) => state.traderDashboard);
  const { profile } = useSelector((state) => state.auth);
  const { isChatOpen, isChatDocked } = useSelector((state) => state.chat);
  const withDockedChat =
    isWiseAiEnabled() && isChatDocked && isChatOpen && profile?.role !== 'ROLE_OPEN_PROFILE';

  // W panelu handlowca chat ZAWSZE otwiera sie jako side panel po prawej (`docked`).
  // Wymuszamy tryb przy KAZDYM otwarciu chatu, bo `setIsChatOpen` reducer przywraca
  // ostatni zapamietany tryb (ktory moze byc `floating` jezeli user otwieral
  // wczesniej chat poza panelem handlowca).
  useEffect(() => {
    if (!isWiseAiEnabled()) return;
    if (isChatOpen && !isChatDocked) {
      dispatch(reduxActions.setChatModeDirect('docked'));
    }
  }, [isChatOpen, isChatDocked, dispatch]);

  const reduxMatchesPath =
    isValidPathClientId &&
    currentClientId === pathClientId &&
    currentClient?.id === pathClientId;

  const shouldHydrateClient = isClientContext && isValidPathClientId && !reduxMatchesPath;

  const { data: hydratedClient, isLoading: isHydratingClient } = useGetTraderClient(pathClientId, {
    enabled: shouldHydrateClient
  });

  useEffect(() => {
    if (!isClientContext) {
      dispatch(reduxActions.setCurrentClient({ clientId: null, client: null }));
    }
  }, [dispatch, isClientContext]);

  useEffect(() => {
    if (hydratedClient && shouldHydrateClient && hydratedClient.id === pathClientId) {
      dispatch(reduxActions.setCurrentClient({ clientId: pathClientId, client: hydratedClient }));
    }
  }, [dispatch, hydratedClient, pathClientId, shouldHydrateClient]);

  const showClientHydrationLoader = shouldHydrateClient && isHydratingClient;

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    // Po zmianie ścieżki na mobile zamknij drawer (gdy user kliknął w pozycję menu).
    setIsMobileMenuOpen(false);
  }, [pathname]);

  return (
    <>
      <Helmet>
        <link
          rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.13.1/font/bootstrap-icons.min.css"
        />
      </Helmet>
      <div
        className={classnames(styles.layoutFlex, {
          [styles.withDockedChat]: withDockedChat
        })}>
      <div
        className={classnames(
          styles.componentWrapper,
          'StylePath-Components-Layouts-TraderPanelLayout'
        )}>
        <main>
          <header>
            <HeaderTopBar simpleHeader={false} isTraderPanel />
          </header>
          {isMobile && (
            <button
              type="button"
              aria-label="Menu"
              className={styles.hamburgerButton}
              onClick={() => setIsMobileMenuOpen((v) => !v)}>
              <MenuIcon />
            </button>
          )}
          {isMobile && isMobileMenuOpen && (
            <div
              className={styles.menuBackdrop}
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden
            />
          )}
          <div ref={contentRef} className={styles.content}>
            <Grid container columnSpacing="32px">
              <Grid
                item
                md={isCondensed ? 1 : 2.5}
                xs={12}
                className={classnames(styles.menuWrapper, {
                  [styles.menuWrapperCondensed]: isCondensed,
                  [styles.menuWrapperOpen]: isMobileMenuOpen
                })}>
                {isClientContext ? (
                  <TraderClientDashboardMenu
                    isCondensed={isCondensed}
                    onToggleCondensed={() => setIsCondensed((v) => !v)}
                  />
                ) : (
                  <WiseTraderDashboardMenu
                    isCondensed={isCondensed}
                    onToggleCondensed={() => setIsCondensed((v) => !v)}
                  />
                )}
              </Grid>
              <Grid item md={isCondensed ? 11 : 9.5} xs={12}>
                {showClientHydrationLoader ? (
                  <Loader />
                ) : (
                  <Suspense fallback={<Loader />}>
                    <Outlet />
                  </Suspense>
                )}
              </Grid>
            </Grid>
          </div>
          <BottomMenu />
        </main>
      </div>
        {withDockedChat && <DockedChatContainer />}
      </div>
      {/* Launcher widoczny TYLKO gdy chat zamkniety — gdy chat docked-otwarty, ma
          swoj wlasny krzyzyk w naglowku i launcher tylko zaslanialby pole inputu. */}
      {isWiseAiEnabled() && !isChatOpen && (
        <div className={styles.chatLauncherFloating}>
          <ChatLauncher />
        </div>
      )}
    </>
  );
};

export default TraderPanelLayout;
